import type { OnChanges, SimpleChanges } from '@angular/core';
import type { AcceptEnum } from '@commons/bo-ui/form/file-picker/models/accept.enum';
import type { RejectedFilesCount } from '@commons/bo-ui/form/file-picker/models/rejected-files-count';

import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { FileTypePipe } from '@commons/bo-ui/form/file-picker/pipes/file-type.pipe';

@Component({
  selector: 'app-file-picker-placeholder',
  templateUrl: './placeholder.component.html',
  styleUrls: ['./placeholder.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButton, FileTypePipe],
})
export class PlaceholderComponent implements OnChanges {
  @Input() id!: string;
  @Input() accept!: AcceptEnum[];
  @Input() maxSizePerFileInMB!: number;
  @Input() width?: number;
  @Input() height?: number;
  @Input() caption?: string;
  @Output() filesChosen = new EventEmitter<File[]>();
  @Output() rejectedFilesCount = new EventEmitter<RejectedFilesCount>();

  #multiUpload!: boolean;
  @Input() set multiUpload(isMultiUpload: boolean) {
    this.#multiUpload = isMultiUpload;
    if (isMultiUpload) {
      this.defaultCaption = 'Déposez vos fichiers ici';
      this.labelCta = 'Sélectionnez des fichiers';
      this.labelHint = 'Fichiers';
    } else {
      this.defaultCaption = 'Déposez votre fichier ici';
      this.labelCta = 'Sélectionnez un fichier';
      this.labelHint = 'Fichier';
    }
  }

  get multiUpload(): boolean {
    return this.#multiUpload;
  }

  computedAccept!: string;
  defaultCaption!: string;
  labelCta!: string;
  labelHint!: string;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['accept']) {
      this.computedAccept = changes['accept'].currentValue ? changes['accept'].currentValue.join(', ') : undefined;
    }
  }

  async onFilesInput(event: Event): Promise<void> {
    const chosenFiles: FileList = (event.target as HTMLInputElement).files!;
    const candidateFiles: File[] = [];
    for (const chosenFile of chosenFiles) {
      if (this.#isFileTypeAccepted(chosenFile)) {
        candidateFiles.push(chosenFile);
      }
    }

    const rejectedFilesCount: RejectedFilesCount = {
      maxSizeExceeded: 0,
      fileTooSmall: 0,
    };

    const acceptedFiles = await Promise.all(
      candidateFiles.map(async (file) => {
        const isSizeLimitExceeded = this.#isFileSizeLimitExceeded(file, rejectedFilesCount);
        const isWidthOrHeightLimitExceeded = await this.#isFileWidthOrHeightLimitExceeded(file, rejectedFilesCount);

        if (isSizeLimitExceeded || isWidthOrHeightLimitExceeded) {
          return null;
        } else {
          return file;
        }
      })
    );

    const filteredAcceptedFiles = acceptedFiles.filter((file) => file !== null);

    this.rejectedFilesCount.emit(rejectedFilesCount);

    if (filteredAcceptedFiles.length) {
      this.filesChosen.emit(acceptedFiles as File[]);
    }
  }

  #isFileTypeAccepted(file: File): boolean {
    return this.accept.includes(file.type as AcceptEnum);
  }

  #isFileSizeLimitExceeded(file: File, rejectedFilesCount: RejectedFilesCount): boolean {
    const fileSizeLimitExceeded = file.size / 1024 / 1024 > this.maxSizePerFileInMB;
    if (fileSizeLimitExceeded) {
      rejectedFilesCount.maxSizeExceeded++;
    }

    return fileSizeLimitExceeded;
  }

  async #isFileWidthOrHeightLimitExceeded(file: File, rejectedFilesCount: RejectedFilesCount): Promise<boolean> {
    if (!this.width || !this.height) return false;

    const url = URL.createObjectURL(file);

    try {
      const dimensions: { width: number; height: number } = await this.#getHeightAndWidthFromDataUrl(url);

      if (dimensions.height < this.height || dimensions.width < this.width) {
        rejectedFilesCount.fileTooSmall++;
        return true;
      }

      return false;
    } catch (error) {
      throw new Error(error as string, { cause: error });
    }
  }

  #getHeightAndWidthFromDataUrl = (dataURL: string): Promise<{ width: number; height: number }> =>
    new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({
          height: img.height,
          width: img.width,
        });
      };
      img.src = dataURL;
    });
}
